import React, { useState, useEffect, useRef } from 'react';
import { Play, Eye, Search, Plus, Upload, X, Loader2, Maximize2, Trash2, Edit2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { getTimeAgo } from '../lib/dateUtils';

// Format counts
const formatCount = (count) => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count?.toString() || '0';
};

// Video Upload Modal
const VideoUploadModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    category: 'general'
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      setError('File too large. Maximum size is 100MB.');
      return;
    }

    setUploading(true);
    setError('');
    
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      
      const response = await axios.post(`${API}/upload-video`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      const videoUrl = response.data.video_url.startsWith('http') 
        ? response.data.video_url 
        : `${BACKEND_URL}${response.data.video_url}`;
      
      setFormData(prev => ({ ...prev, video_url: videoUrl }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.video_url.trim()) {
      setError('Video URL is required');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.post(`${API}/videos`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit video');
    } finally {
      setUploading(false);
    }
  };

  const getEmbedUrl = (url) => {
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upload Video</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Video</label>
            
            {!formData.video_url ? (
              <div className="space-y-3">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-500 transition-colors"
                >
                  {uploading ? (
                    <div className="space-y-2">
                      <Loader2 className="h-10 w-10 mx-auto text-emerald-500 animate-spin" />
                      <p className="text-sm text-gray-500">Uploading... {uploadProgress}%</p>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Click to upload video file</p>
                      <p className="text-xs text-gray-400 mt-1">MP4, WebM, MOV up to 100MB</p>
                    </>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="video/*" onChange={handleFileUpload} className="hidden" />

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                  <span className="text-xs text-gray-500">or paste URL</span>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                </div>

                <input
                  type="text"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <div className="aspect-video bg-black rounded-xl overflow-hidden">
                  {formData.video_url.includes('youtube.com') || formData.video_url.includes('vimeo.com') || formData.video_url.includes('youtu.be') ? (
                    <iframe src={getEmbedUrl(formData.video_url)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  ) : (
                    <video src={formData.video_url} controls className="w-full h-full" />
                  )}
                </div>
                <button type="button" onClick={() => setFormData({ ...formData, video_url: '' })} className="text-sm text-red-500 hover:text-red-600">
                  Remove video
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter video title"
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe your video..."
              rows={3}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl">
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !formData.video_url || !formData.title}
              className="px-6 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Submit for Review
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Video Edit Modal
const VideoEditModal = ({ video, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: video.title || '',
    description: video.description || '',
    video_url: video.video_url || '',
    thumbnail_url: video.thumbnail_url || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      setError('File too large. Maximum size is 100MB.');
      return;
    }

    setUploading(true);
    setError('');
    
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      
      const response = await axios.post(`${API}/upload-video`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      const videoUrl = response.data.video_url.startsWith('http') 
        ? response.data.video_url 
        : `${BACKEND_URL}${response.data.video_url}`;
      
      setFormData(prev => ({ ...prev, video_url: videoUrl }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.video_url.trim()) {
      setError('Video URL is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.put(`${API}/videos/${video.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onSuccess({ ...video, ...formData });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update video');
    } finally {
      setSaving(false);
    }
  };

  const getEmbedUrl = (url) => {
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Video</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Video</label>
            
            {!formData.video_url ? (
              <div className="space-y-3">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-500 transition-colors"
                >
                  {uploading ? (
                    <div className="space-y-2">
                      <Loader2 className="h-10 w-10 mx-auto text-emerald-500 animate-spin" />
                      <p className="text-sm text-gray-500">Uploading... {uploadProgress}%</p>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Click to upload new video</p>
                      <p className="text-xs text-gray-400 mt-1">MP4, WebM, MOV up to 100MB</p>
                    </>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="video/*" onChange={handleFileUpload} className="hidden" />

                <input
                  type="text"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="Or paste video URL..."
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <div className="aspect-video bg-black rounded-xl overflow-hidden">
                  {formData.video_url.includes('youtube.com') || formData.video_url.includes('vimeo.com') || formData.video_url.includes('youtu.be') ? (
                    <iframe src={getEmbedUrl(formData.video_url)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  ) : (
                    <video src={formData.video_url} controls className="w-full h-full" />
                  )}
                </div>
                <button type="button" onClick={() => setFormData({ ...formData, video_url: '' })} className="text-sm text-red-500 hover:text-red-600">
                  Replace video
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter video title"
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe your video..."
              rows={3}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.video_url || !formData.title}
              className="px-6 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Edit2 className="h-4 w-4" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Video Player Modal - Simple overlay with fullscreen
const VideoPlayerModal = ({ video, onClose }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  // Get embed URL for YouTube/Vimeo
  const getEmbedUrl = (url) => {
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    return null;
  };

  const embedUrl = getEmbedUrl(video.video_url);
  const isEmbed = !!embedUrl;

  const handleFullscreen = () => {
    if (containerRef.current) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      }
    }
  };

  // Close on escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={onClose}>
      <div 
        ref={containerRef}
        className="relative w-full max-w-5xl mx-4" 
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
        >
          <X className="h-8 w-8" />
        </button>

        {/* Fullscreen button */}
        <button 
          onClick={handleFullscreen}
          className="absolute -top-12 right-12 p-2 text-white/70 hover:text-white transition-colors"
          title="Fullscreen"
        >
          <Maximize2 className="h-6 w-6" />
        </button>

        {/* Video Player */}
        <div className="aspect-video bg-black rounded-xl overflow-hidden shadow-2xl">
          {isEmbed ? (
            <iframe
              src={embedUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          ) : (
            <video 
              ref={videoRef}
              src={video.video_url} 
              controls 
              autoPlay 
              className="w-full h-full"
            />
          )}
        </div>

        {/* Video Info */}
        <div className="mt-4 text-white">
          <h2 className="text-xl font-bold">{video.title}</h2>
          <p className="text-white/60 text-sm mt-1">
            {video.author} • {getTimeAgo(video.created_at)}
          </p>
          {video.description && (
            <p className="text-white/80 text-sm mt-3">{video.description}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Video Card for Grid
const VideoCard = ({ video, onClick, canEdit, onDelete, onEdit, currentUserId }) => {
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  // User can edit if they are admin or the video owner
  const canEditVideo = canEdit && (video.author_id === currentUserId || canEdit === 'admin');

  const handleDelete = async (e) => {
    e.stopPropagation();
    setDeleting(true);
    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.delete(`${API}/videos/${video.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onDelete?.(video.id);
    } catch (err) {
      console.error('Failed to delete video:', err);
      alert('Failed to delete video');
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="group relative">
      {/* Edit/Delete Buttons - visible for admin or video owner */}
      {canEditVideo && (
        <div className="absolute top-2 right-2 z-10 flex gap-1">
          {showConfirm ? (
            <div className="flex items-center gap-1 bg-white dark:bg-gray-900 rounded-lg shadow-lg p-1 border border-gray-200 dark:border-gray-700" onClick={e => e.stopPropagation()}>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 flex items-center gap-1"
              >
                {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Delete'}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setShowConfirm(false); }}
                className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                Cancel
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit?.(video); }}
                className="p-2 bg-emerald-500/90 hover:bg-emerald-600 text-white rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                title="Edit video"
              >
                <Edit2 className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
                className="p-2 bg-red-500/90 hover:bg-red-600 text-white rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete video"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      )}

      <div onClick={() => onClick(video)} className="cursor-pointer">
        <div className="relative aspect-video bg-gray-200 dark:bg-gray-800 rounded-xl overflow-hidden mb-3 shadow-sm">
          <img
            src={video.thumbnail_url || 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?w=800&h=450&fit=crop'}
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?w=800&h=450&fit=crop'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-14 h-14 bg-white/95 rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
              <Play className="h-7 w-7 text-emerald-600 ml-1" fill="currentColor" />
            </div>
          </div>
          {/* Duration badge */}
          <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 text-white text-xs font-medium rounded">
            {video.duration || '0:00'}
          </div>
        </div>
        
        <div className="flex gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <span className="text-white text-sm font-semibold">{video.author?.[0]?.toUpperCase() || 'U'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 dark:text-white line-clamp-2 group-hover:text-emerald-500 transition-colors text-sm leading-snug">
              {video.title}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              {video.author} • {getTimeAgo(video.created_at)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const VideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const { t, isKinyarwanda } = useLanguage();
  const { isAuthenticated, isAdmin, user } = useAuth();

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API}/videos?status=published&limit=50`);
      setVideos(res.data.videos || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVideo = (videoId) => {
    setVideos(prev => prev.filter(v => v.id !== videoId));
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
  };

  const handleVideoUpdated = (updatedVideo) => {
    setVideos(prev => prev.map(v => v.id === updatedVideo.id ? updatedVideo : v));
  };

  const filteredVideos = videos.filter(video =>
    video.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    video.author?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    video.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const canUpload = isAuthenticated;
  // Determine edit permission level: 'admin' for admins, true for contributors, false otherwise
  const canEditLevel = isAdmin ? 'admin' : (isAuthenticated ? true : false);
  const currentUserId = user?.id;

  const pageTitle = isKinyarwanda ? 'Amashusho' : 'Videos';
  const searchPlaceholder = isKinyarwanda ? 'Shakisha amashusho...' : 'Search videos...';
  const noVideosText = isKinyarwanda ? 'Nta mashusho ahari' : 'No videos yet';
  const noVideosDesc = isKinyarwanda ? 'Ba uwa mbere wohereza amashusho!' : 'Be the first to upload a video!';
  const uploadText = isKinyarwanda ? 'Ohereza Video' : 'Upload Video';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Upload button only - aligned right */}
      {canUpload && (
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">{uploadText}</span>
          </button>
        </div>
      )}

      {/* Video Grid */}
      {filteredVideos.length === 0 ? (
        <div className="text-center py-16">
          <Play className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{noVideosText}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{noVideosDesc}</p>
          {canUpload && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="mt-4 px-6 py-2 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors"
            >
              {uploadText}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {filteredVideos.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              onClick={setSelectedVideo}
              canEdit={canEditLevel}
              currentUserId={currentUserId}
              onDelete={handleDeleteVideo}
              onEdit={handleEditVideo}
            />
          ))}
        </div>
      )}

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayerModal
          video={selectedVideo}
          onClose={() => setSelectedVideo(null)}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <VideoUploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={fetchVideos}
        />
      )}

      {/* Edit Modal */}
      {editingVideo && (
        <VideoEditModal
          video={editingVideo}
          onClose={() => setEditingVideo(null)}
          onSuccess={handleVideoUpdated}
        />
      )}
    </div>
  );
};

export default VideosPage;
